import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, Loader2, CheckCircle2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${(parseFloat(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Owner-authorized, generalized production money repair (Sept 2026) — scans ALL bookings for
// duplicate "payment storms" and synthetic/test entries. Dry-run is always read-only; Apply
// requires the management password and reverses rows using the same audit-safe mechanism as
// the single-row Reverse Entry tool (never deletes, never refunds).
export default function MoneyRepairCard({ token }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [applied, setApplied] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadDryRun = async () => {
    setLoading(true);
    setApplied(null);
    try {
      const { data } = await axios.get(`${API}/finance/repair/dry-run`, auth);
      setReport(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load the money repair dry-run."); }
    finally { setLoading(false); }
  };

  const apply = async () => {
    if (confirmText.trim().toUpperCase() !== "APPLY REPAIR") { toast.error('Type "APPLY REPAIR" exactly to confirm.'); return; }
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/finance/repair/apply`, { confirm_phrase: confirmText }, auth);
      setApplied(data);
      setConfirmText("");
      toast.success(data.reversed_count > 0
        ? `Reversed ${data.reversed_count} row(s) totaling ${money(data.total_amount_removed)}.`
        : "Already clean — nothing to reverse.");
      loadDryRun();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply the money repair."); }
    finally { setApplying(false); }
  };

  return (
    <div data-testid="money-repair-card" className="rounded-lg border border-red-500/30 bg-[#0C0C0C] p-4 space-y-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-red-400">
        <ShieldAlert size={14} /> Owner-Authorized Money Repair — All Bookings
      </div>

      <button data-testid="money-repair-dry-run-btn" onClick={loadDryRun} disabled={loading}
        className="text-xs font-semibold border border-red-500/40 text-red-300 px-3 py-1.5 rounded hover:bg-red-500 hover:text-black transition-colors disabled:opacity-50 flex items-center gap-1.5">
        {loading ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Run Dry-Run (read-only)
      </button>

      {report && (
        <div data-testid="money-repair-report" className="space-y-2">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="bg-black/40 border border-white/10 rounded p-2">
              <div className="text-white/40 text-[10px] uppercase">Duplicate Storms</div>
              <div className="text-lg font-black text-red-300">{report.duplicate_storm_count}</div>
            </div>
            <div className="bg-black/40 border border-white/10 rounded p-2">
              <div className="text-white/40 text-[10px] uppercase">Test Entries</div>
              <div className="text-lg font-black text-red-300">{report.test_synthetic_entries.length}</div>
            </div>
            <div className="bg-black/40 border border-white/10 rounded p-2">
              <div className="text-white/40 text-[10px] uppercase">Total To Reverse</div>
              <div className="text-lg font-black text-[#F2B705]">{money(report.total_amount_to_reverse)}</div>
            </div>
            <div className="bg-black/40 border border-white/10 rounded p-2">
              <div className="text-white/40 text-[10px] uppercase">Bookings Affected</div>
              <div className="text-lg font-black text-white">{report.affected_booking_count}</div>
            </div>
          </div>

          {report.duplicate_storms.length > 0 && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="money-repair-storm-list">
              {report.duplicate_storms.map((s) => (
                <div key={s.canonical_id} className="text-[11px] flex items-center justify-between bg-red-500/5 border border-red-500/15 rounded px-2 py-1.5">
                  <span className="text-white/70">{s.customer || s.booking_id} · {s.count}x {money(s.amount_each)} {s.type} on {s.date}</span>
                  <span className="text-red-300 font-semibold">-{money(s.total_excess_to_remove)}</span>
                </div>
              ))}
            </div>
          )}

          <div className="text-[11px] text-white/50 pt-1">{report.note}</div>

          {report.paid_over_charges_bookings?.length > 0 && (
            <div data-testid="money-repair-overcharge-list" className="space-y-1">
              <div className="text-[11px] uppercase tracking-wider text-amber-300">Bookings with Paid &gt; Charges — review ({report.paid_over_charges_bookings.length})</div>
              <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                {report.paid_over_charges_bookings.map((o) => (
                  <div key={o.booking_id} className="text-[11px] flex items-center justify-between bg-amber-500/5 border border-amber-500/15 rounded px-2 py-1.5">
                    <span className="text-white/70">{o.customer || o.booking_id} · Paid {money(o.amount_paid)} vs Charges {money(o.total_price - o.discount)}</span>
                    <span className="text-amber-300 font-semibold">Credit {money(o.credit)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {applied?.ok ? (
            <div className="flex items-center gap-2 text-emerald-400 text-xs"><CheckCircle2 size={13} /> Applied — {applied.reversed_count} row(s) reversed, {money(applied.total_amount_removed)} removed across {applied.affected_booking_ids.length} booking(s).</div>
          ) : (
            <div className="flex items-center gap-2 pt-1">
              <input data-testid="money-repair-password" type="text" value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)} placeholder='Type "APPLY REPAIR" to confirm'
                className="bg-black/40 border border-white/15 rounded px-2 py-1 text-xs text-white flex-1" />
              <button data-testid="money-repair-apply-btn" onClick={apply} disabled={applying || report.total_amount_to_reverse <= 0}
                className="text-xs font-semibold border border-emerald-500/40 text-emerald-300 px-3 py-1.5 rounded hover:bg-emerald-500 hover:text-black transition-colors disabled:opacity-50 whitespace-nowrap">
                {applying ? <Loader2 className="animate-spin inline" size={12} /> : "Confirm & Apply Repair"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
